/**
 * proHome store — session cache for the marketplace-first Home (plan 443):
 * the Home summary (`GET /api/marketplace/pro/home`) and the pipeline summary
 * (`GET /api/professional/pipeline-summary`).
 *
 * Follows the module-level idiom of `stores/today.ts`: a zustand store plus
 * module-level in-flight promises, so any number of mounted consumers (Home,
 * the header) share ONE request per summary.
 *
 * Freshness, with no interval polling:
 *   - invalidated by `bumpMarketplaceRequests` (Home summary), the
 *     `marketplace_setup` dirty event (Home summary) and Today item mutations
 *     (both). An invalidation refetches at once while a consumer is mounted;
 *     otherwise it marks the data stale and the next mount refetches. One that
 *     lands during a fetch queues exactly one follow-up fetch.
 *   - window focus refetches a summary only when it is older than 60 s.
 *
 * The Home summary is marketplace data: it is requested only while a mounted
 * consumer reports the go-live gate `on`. With the gate `off` or `loading`
 * the store issues ZERO marketplace requests. The pipeline summary is a
 * professional/CRM read and loads regardless of the gate, but only while a
 * mounted consumer asks for it: the header (mounted on every route) needs just
 * the Home summary for its badge and the paused banner, so only Home attaches
 * with `pipeline: true`.
 *
 * A failed fetch keeps the last good data and reports `error`; Home sections
 * degrade, the store does not.
 */
import { create } from "zustand";
import { useEffect } from "react";

import { getPipelineSummary, getProHomeSummary } from "../lib/api";
import { IS_PREVIEW } from "../lib/pro-mode";
import { loadPreviewHomeFixture } from "../lib/pro-home-preview";
import {
  PRO_RESOURCES_DIRTY_EVENT,
  type ProResourcesDirtyDetail,
} from "../lib/pro-resource-dirty";
import type { PipelineSummary, ProHomeSummary } from "../lib/pro-home-types";
import { useProToken, type GetProToken } from "../hooks/useProToken";
import type { MarketplaceGate } from "../hooks/useMarketplaceGate";
import { useMarketplaceRequestListStore } from "./marketplaceRequestList";
import { useMarketplaceRequestsRefreshStore } from "./marketplaceRequests";
import { onTodayMutation } from "./today";

/** A cached value older than this is refetched on window focus. */
export const FOCUS_STALE_MS = 60_000;

type SummaryKey = "home" | "pipeline";

export interface SummarySlice<T> {
  /** Null until a fetch has succeeded (or the `flag-off` preview fixture). */
  data: T | null;
  error: string | null;
  /** First load in flight, nothing to show yet. */
  isLoading: boolean;
  /** A refetch is in flight over data already shown. */
  isRefreshing: boolean;
  hasLoaded: boolean;
  /** `Date.now()` of the last successful fetch. */
  fetchedAt: number | null;
  /** Invalidated, or the last fetch failed: the next ensure/focus refetches. */
  stale: boolean;
}

interface ProHomeStoreState {
  home: SummarySlice<ProHomeSummary>;
  pipeline: SummarySlice<PipelineSummary>;
  /** Register a mounted consumer; returns its detach. Loads what is missing. */
  attach: (consumer: Consumer) => () => void;
  /** Mark summaries stale and refetch them now if a consumer is mounted. */
  invalidate: (keys?: SummaryKey[]) => void;
  /** Refetch any summary older than `FOCUS_STALE_MS` (or stale). */
  revalidateOnFocus: () => void;
  reset: () => void;
}

export interface Consumer {
  getToken: GetProToken;
  /** True only while the marketplace go-live gate is `on`. */
  marketplace: boolean;
  /** Wants the pipeline summary (Home). Defaults to true when omitted. */
  pipeline?: boolean;
}

function emptySlice<T>(): SummarySlice<T> {
  return {
    data: null,
    error: null,
    isLoading: false,
    isRefreshing: false,
    hasLoaded: false,
    fetchedAt: null,
    stale: false,
  };
}

const consumers = new Set<Consumer>();
const inFlight: Record<SummaryKey, Promise<void> | null> = {
  home: null,
  pipeline: null,
};
const refetchQueued: Record<SummaryKey, boolean> = {
  home: false,
  pipeline: false,
};
// Bumped by reset() so a fetch that outlives it cannot write into fresh state.
let generation = 0;
let focusListening = false;

const ALL_KEYS: SummaryKey[] = ["home", "pipeline"];

/** Whether a fetch of `key` may run right now. */
function canLoad(key: SummaryKey): boolean {
  if (consumers.size === 0) return false;
  for (const consumer of consumers) {
    if (key === "pipeline" ? consumer.pipeline !== false : consumer.marketplace) return true;
  }
  return false;
}

function anyToken(): GetProToken {
  const [first] = consumers;
  return first ? first.getToken : () => Promise.resolve(null);
}

async function previewFixtureFor(key: SummaryKey) {
  const fixture = await loadPreviewHomeFixture();
  return key === "home" ? fixture.home : fixture.pipeline;
}

async function fetchSummary(key: SummaryKey): Promise<ProHomeSummary | PipelineSummary | null> {
  if (IS_PREVIEW) return previewFixtureFor(key);
  const token = (await anyToken()()) ?? undefined;
  return key === "home" ? getProHomeSummary(token) : getPipelineSummary(token);
}

function load(key: SummaryKey): Promise<void> {
  const running = inFlight[key];
  if (running) return running;
  const gen = generation;
  const patch = (next: Partial<SummarySlice<ProHomeSummary & PipelineSummary>>) => {
    if (gen !== generation) return;
    useProHomeStore.setState((state) => ({ [key]: { ...state[key], ...next } }));
  };
  const current = useProHomeStore.getState()[key];
  patch({ isLoading: !current.hasLoaded, isRefreshing: current.hasLoaded, error: null });

  const run = (async () => {
    try {
      const data = await fetchSummary(key);
      patch({
        data: data as never,
        error: null,
        isLoading: false,
        isRefreshing: false,
        hasLoaded: true,
        fetchedAt: Date.now(),
        stale: false,
      });
    } catch (err) {
      patch({
        error: err instanceof Error ? err.message : "Failed to load Home",
        isLoading: false,
        isRefreshing: false,
        stale: true,
      });
    }
  })().finally(() => {
    if (gen !== generation) return;
    inFlight[key] = null;
    if (!refetchQueued[key]) return;
    refetchQueued[key] = false;
    if (canLoad(key)) void load(key);
    else patch({ stale: true });
  });
  inFlight[key] = run;
  return run;
}

function isDue(slice: SummarySlice<unknown>, now: number): boolean {
  return (
    slice.stale ||
    !slice.hasLoaded ||
    slice.fetchedAt === null ||
    now - slice.fetchedAt > FOCUS_STALE_MS
  );
}

function onWindowFocus() {
  useProHomeStore.getState().revalidateOnFocus();
}

function syncFocusListener() {
  if (typeof window === "undefined") return;
  if (consumers.size > 0 && !focusListening) {
    window.addEventListener("focus", onWindowFocus);
    focusListening = true;
  } else if (consumers.size === 0 && focusListening) {
    window.removeEventListener("focus", onWindowFocus);
    focusListening = false;
  }
}

export const useProHomeStore = create<ProHomeStoreState>((set, get) => ({
  home: emptySlice<ProHomeSummary>(),
  pipeline: emptySlice<PipelineSummary>(),

  attach: (consumer) => {
    consumers.add(consumer);
    syncFocusListener();
    for (const key of ALL_KEYS) {
      if (!canLoad(key)) continue;
      const slice = get()[key];
      if (!slice.hasLoaded || slice.stale) void load(key);
    }
    return () => {
      consumers.delete(consumer);
      syncFocusListener();
    };
  },

  invalidate: (keys = ALL_KEYS) => {
    for (const key of keys) {
      if (inFlight[key]) {
        refetchQueued[key] = true;
      } else if (canLoad(key)) {
        void load(key);
      } else {
        set((state) => ({ [key]: { ...state[key], stale: true } }));
      }
    }
  },

  revalidateOnFocus: () => {
    const now = Date.now();
    for (const key of ALL_KEYS) {
      if (!canLoad(key) || inFlight[key]) continue;
      if (isDue(get()[key], now)) void load(key);
    }
  },

  reset: () => {
    generation += 1;
    consumers.clear();
    syncFocusListener();
    inFlight.home = null;
    inFlight.pipeline = null;
    refetchQueued.home = false;
    refetchQueued.pipeline = false;
    useMarketplaceRequestListStore.getState().reset();
    set({ home: emptySlice<ProHomeSummary>(), pipeline: emptySlice<PipelineSummary>() });
  },
}));

// ─────────────────────────────────────────────────────────────────────────
// Invalidation sources (module-level, installed once).
// ─────────────────────────────────────────────────────────────────────────

// A confirm/decline on the Requests surfaces changes the pending count, the
// hero and the needs stream.
useMarketplaceRequestsRefreshStore.subscribe((state, prev) => {
  if (state.version !== prev.version) useProHomeStore.getState().invalidate(["home"]);
});

// Done / snooze / dismiss / add on a Today item.
onTodayMutation(() => useProHomeStore.getState().invalidate());

// Setup changes (availability, standing offers, service area, card) elsewhere
// in the app.
if (typeof window !== "undefined") {
  window.addEventListener(PRO_RESOURCES_DIRTY_EVENT, (event) => {
    const detail = (event as CustomEvent<ProResourcesDirtyDetail>).detail;
    if (detail?.resources?.includes("marketplace_setup")) {
      useProHomeStore.getState().invalidate(["home"]);
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Hook.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Home / header consumer. Pass `useMarketplaceGate()`; the Home summary loads
 * only while it is `on`. The pipeline summary loads regardless of the gate, but
 * only for a caller that wants it (`pipeline`, default true): the header passes
 * `false` so a route that is not Home never pays for it.
 */
export function useProHome(gate: MarketplaceGate, { pipeline: wantsPipeline = true } = {}) {
  const getToken = useProToken();
  const marketplace = gate === "on";
  useEffect(
    () => useProHomeStore.getState().attach({ getToken, marketplace, pipeline: wantsPipeline }),
    [getToken, marketplace, wantsPipeline],
  );
  const home = useProHomeStore((s) => s.home);
  const pipeline = useProHomeStore((s) => s.pipeline);
  return { home, pipeline };
}
